// src/pages/CuratedTripPage.jsx
// routechef.com/trips/:slug  ·  a set itinerary the traveller can reshape.
//
// Three ways to edit, all going through tripEditor.applyOps():
//   1. "Edit with AI" chat (agent Lambda, local parser fallback)
//   2. Inline: remove an activity, + Add from the curated pool, Change hotel
//   3. The controls bar: from, date, travellers, optimise for
// Getting there / back and stays come from creatorTripEngine (same as creator trips).
// "Book this trip" opens the early-access sheet (same as TripPlanner) until one-tap booking
// for the whole trip is live. Live hotels can still be booked one by one from their stay row.
// Uses the site Navbar (App.js Layout); the page leaves room for it at the top.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Sparkles, X, Plus, RotateCcw, Undo2, Pencil } from "lucide-react";
import { FEATURES } from "../config/features";
import { getCuratedTrip, TIER_LABEL, defaultOriginFor, DEFAULT_CURATED_SEARCH_MODES } from "../curated/curatedTripsData";
import { ORIGIN_CITIES } from "../creator/creatorTripsData";
import { searchStayHotels, handoffLiveHotel, stayDates } from "../curated/hotelSearch";
import { openInNewTab } from "../utils/bookingTab";
import {
    applyOps, runTripAgent, priceTrip, tripSnapshot, stayBlocks, hotelOptionsFor, allBlocks,
} from "../curated/tripEditor";
import {
    buildInstance, repriceInstance, addDays, fmtDay, fmtTime, inr, toISO, segMode, segName,
} from "../creator/creatorTripEngine";
import { EdgeLeg, Section, Field, StickyControls, tripStyles as BASE, MODE_ICON, BLOCK_ICON } from "./CreatorTripPage";
import HotelPickerSheet from "../components/curated/HotelPickerSheet";
import EarlyAccessSheet from "../components/curated/EarlyAccessSheet";
import TripAgentChat from "../components/curated/TripAgentChat";
import { useTripLeadSignals, requestBotOpen } from "../components/lead-bot";

const NAV_H = 72; // fixed site Navbar height
const ORANGE = "#FF5B15";
const INK = "#0F172A";
const MUTED = "#64748B";
const GREEN = "#059669";
const AMBER = "#B45309";

const ENABLED = FEATURES.ENABLE_CURATED_TRIPS ?? FEATURES.ENABLE_CREATOR_TRIPS;
const STORE_KEY = (slug) => `rc_curated_trip_${slug}_v2`;
// Routes are searched live by default on curated trips. Set CURATED_TRIPS_LIVE_SEARCH: false to use estimates.
const LIVE_ROUTES = FEATURES.CURATED_TRIPS_LIVE_SEARCH ?? true;
// Chat input: options only for now (no free text). Set CURATED_TRIP_CHAT_FREEFORM: true to bring the text box back.
const CHAT_FREEFORM = FEATURES.CURATED_TRIP_CHAT_FREEFORM ?? false;
const LOCK_LABEL = { LOCKED: "Curator pick", PREFERRED: "Preferred", FLEXIBLE: "Flexible" };

let msgSeq = 0;
const mid = () => `c${++msgSeq}`;

const initialState = (trip) => ({
    trip,
    hotelPicks: {},
    liveHotels: {},
    // Start from the trip's natural gateway (Coorg from Bengaluru, Rishikesh from Delhi...). User can change it.
    params: { originName: defaultOriginFor(trip)?.name || ORIGIN_CITIES[0]?.name, startDate: toISO(addDays(new Date(), 7)), pax: 2, routePref: "best" },
});

const loadSaved = (trip) => {
    try {
        const raw = localStorage.getItem(STORE_KEY(trip.slug));
        if (!raw) return null;
        const s = JSON.parse(raw);
        // Keep catalog data fresh from the code, keep the traveller's days / picks / params
        if (!s?.trip?.days) return null;
        const today = toISO(new Date());
        return {
            trip: { ...trip, days: s.trip.days, exit: { ...trip.exit, day_offset: s.trip.exit?.day_offset ?? trip.exit.day_offset } },
            hotelPicks: s.hotelPicks || {},
            liveHotels: s.liveHotels || {},   // re-checked against fresh results once the hotel search returns
            params: { ...initialState(trip).params, ...s.params, ...(s.params?.startDate <= today ? { startDate: initialState(trip).params.startDate } : {}) },
        };
    } catch { return null; }
};

// What actually needs a fresh engine search: route ends, stays, transfers. Moving or adding an
// activity doesn't, so those edits re-price instantly without hitting live search again.
const structKey = (trip) => JSON.stringify({
    entry: trip.entry, exit: trip.exit,
    s: allBlocks(trip).filter((x) => x.b.type === "STAY" || x.b.type === "MOVE").map((x) => [x.di, x.b.id, x.b.nights || 0, x.b.depart || ""]),
});

const useIsMobile = () => {
    const q = "(max-width: 860px)";
    const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia?.(q).matches);
    useEffect(() => {
        const mq = window.matchMedia?.(q);
        if (!mq) return undefined;
        const on = () => setM(mq.matches);
        mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
        return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
    }, []);
    return m;
};

// Where the fixed site Navbar actually ends. Its height differs by breakpoint and when it shrinks on scroll,
// so a hard-coded 72 left a see-through strip between it and the sticky controls.
const useNavBottom = () => {
    const [h, setH] = useState(NAV_H);
    useEffect(() => {
        let raf = 0;
        const measure = () => {
            raf = 0;
            const nav = document.querySelector("nav.top-nav") || document.querySelector("nav");
            if (!nav) return;
            const pos = window.getComputedStyle(nav).position;
            if (pos !== "fixed" && pos !== "sticky") return;
            const b = Math.max(0, Math.round(nav.getBoundingClientRect().bottom));
            setH((cur) => (b && b !== cur ? b : cur));
        };
        const queue = () => { if (!raf) raf = requestAnimationFrame(measure); };
        measure();
        window.addEventListener("scroll", queue, { passive: true });
        window.addEventListener("resize", queue);
        const nav = document.querySelector("nav.top-nav");
        const ro = nav && typeof ResizeObserver !== "undefined" ? new ResizeObserver(queue) : null;
        ro?.observe(nav);
        return () => { window.removeEventListener("scroll", queue); window.removeEventListener("resize", queue); ro?.disconnect(); if (raf) cancelAnimationFrame(raf); };
    }, []);
    return h;
};

export default function CuratedTripPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const original = useMemo(() => getCuratedTrip(slug), [slug]);
    const isMobile = useIsMobile();
    const navBottom = useNavBottom();
    const stickTop = Math.max(0, navBottom - 1); // 1px tuck under the Navbar, no hairline gap

    const [edit, setEdit] = useState(() => (original ? (loadSaved(original) || initialState(original)) : null));
    const [undoStack, setUndoStack] = useState([]);
    const editRef = useRef(edit);
    editRef.current = edit;

    // Engine instance (getting there / back, stays at live or fallback rates)
    const [baseInst, setInst] = useState(null);
    const [outIdx, setOutIdx] = useState(0);
    const [retIdx, setRetIdx] = useState(0);
    const inst = useMemo(() => (baseInst ? repriceInstance(baseInst, outIdx, retIdx) : null), [baseInst, outIdx, retIdx]);
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState("");
    const [steps, setSteps] = useState({});
    const [barH, setBarH] = useState(120);
    // Sidebar (price card + chat) is one normal column, no inner scroll. If it's taller than the
    // screen it scrolls with the page until its bottom shows, then sticks there.
    const asideRef = useRef(null);
    const [asideH, setAsideH] = useState(0);
    const [viewH, setViewH] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 800));
    useEffect(() => {
        const onResize = () => setViewH(window.innerHeight);
        window.addEventListener("resize", onResize);
        const el = asideRef.current;
        const ro = el && typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => setAsideH(el.offsetHeight)) : null;
        if (el) { setAsideH(el.offsetHeight); ro?.observe(el); }
        return () => { window.removeEventListener("resize", onResize); ro?.disconnect(); };
    }, [isMobile, !!edit]);   // sticky controls height, the sidebar sticks below it   // { out, back, stays: { state, count, source } }
    const reqRef = useRef(0);

    // UI
    const [picker, setPicker] = useState(null);          // stay block id
    const [addingDay, setAddingDay] = useState(null);    // day index with the add list open
    const [chatOpen, setChatOpen] = useState(false);     // mobile sheet
    const [checkout, setCheckout] = useState(false);
    const [checkoutClosed, setCheckoutClosed] = useState(false);
    const [earlySignedUp, setEarlySignedUp] = useState(false);
    const [agentBusy, setAgentBusy] = useState(false);
    const [misses, setMisses] = useState(0);
    const [agentSugs, setAgentSugs] = useState(null);   // follow-ups the AI agent proposed
    const [chat, setChat] = useState(() => [{
        id: mid(), from: "agent",
        text: original ? `This is our ${original.days.length}-day ${original.entry.city} plan. ${CHAT_FREEFORM ? "Tell me what to change: hotels, activities, the number of days, where you start from or who's coming." : "Pick a change below. Dates, start city and travellers are in the bar at the top."}` : "",
    }]);

    const origin = useMemo(
        () => ORIGIN_CITIES.find((c) => c.name === edit?.params.originName) || ORIGIN_CITIES[0],
        [edit?.params.originName],
    );
    const sKey = edit ? structKey(edit.trip) : "";

    // Persist the traveller's version
    useEffect(() => {
        if (!edit) return;
        try {
            localStorage.setItem(STORE_KEY(edit.trip.slug), JSON.stringify({
                trip: { days: edit.trip.days, exit: { day_offset: edit.trip.exit.day_offset } },
                hotelPicks: edit.hotelPicks, params: edit.params,
                liveHotels: Object.fromEntries(Object.entries(edit.liveHotels || {}).map(([k, h]) => [k, { ...h, _raw: undefined }])),
            }));
        } catch { /* storage blocked */ }
    }, [edit]);

    // Rebuild the engine instance only when structure or trip params change
    useEffect(() => {
        if (!edit) return undefined;
        const id = ++reqRef.current;
        setLoading(true);
        setSteps({});
        const t = setTimeout(async () => {
            const cur = editRef.current;
            try {
                const res = await buildInstance(cur.trip, {
                    origin, startDate: new Date(cur.params.startDate + "T00:00:00"), pax: cur.params.pax, routePref: cur.params.routePref,
                    live: LIVE_ROUTES,
                    // Trains matter on Indian leisure routes; same split as SearchForm (direct + Mixed*)
                    searchModes: cur.trip.search_modes || DEFAULT_CURATED_SEARCH_MODES,
                    searchTag: `curated_trip:${cur.trip.slug}`,
                    onProgress: (p) => id === reqRef.current && setProgress(p),
                    onStep: (st) => id === reqRef.current && setSteps((prev) => ({ ...prev, [st.step]: st })),
                });
                if (id !== reqRef.current) return;
                setInst(res);
                setOutIdx(0);
                setRetIdx(0);
            } catch (e) {
                console.error("[CuratedTripPage] buildInstance failed", e);
            } finally {
                if (id === reqRef.current) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [sKey, origin, edit?.params.startDate, edit?.params.pax, edit?.params.routePref]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Live hotels per stay from the /hotels API ─────────────────────────
    const [live, setLive] = useState({});            // { [stayId]: hotel[] }
    const [liveStatus, setLiveStatus] = useState({}); // { [stayId]: "loading" | "done" }
    const liveRef = useRef(live);
    liveRef.current = live;
    const rooms = Math.ceil((edit?.params.pax || 2) / 2);
    const hotelKey = edit ? JSON.stringify([edit.params.startDate, rooms, stayBlocks(edit.trip).map((x) => [x.b.id, x.di, x.b.nights, x.b.city])]) : "";

    useEffect(() => {
        if (!edit) return undefined;
        const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        const stays = stayBlocks(editRef.current.trip);
        const { startDate } = editRef.current.params;
        setLiveStatus(Object.fromEntries(stays.map((x) => [x.b.id, "loading"])));
        stays.forEach(async (x) => {
            const list = await searchStayHotels({
                place: (editRef.current.trip.hotel_search_place || {})[x.b.city] || x.b.city,
                startDate, dayIndex: x.di, nights: x.b.nights, rooms, signal: ctrl?.signal,
            });
            if (ctrl?.signal.aborted) return;
            setLive((l) => ({ ...l, [x.b.id]: list }));
            setLiveStatus((st) => ({ ...st, [x.b.id]: "done" }));
            // A live pick from older dates: refresh its price, or drop it if it's gone
            const cur = editRef.current;
            const code = cur.hotelPicks[x.b.id];
            if (code && code.startsWith("live:")) {
                const fresh = list.find((h) => h.code === code);
                const next = { ...cur, hotelPicks: { ...cur.hotelPicks }, liveHotels: { ...cur.liveHotels } };
                if (fresh) next.liveHotels[x.b.id] = fresh;
                else { delete next.hotelPicks[x.b.id]; delete next.liveHotels[x.b.id]; }
                editRef.current = next;
                setEdit(next);
                if (!fresh) setChat((c) => [...c, { id: mid(), from: "agent", text: `${cur.liveHotels?.[x.b.id]?.name || "Your picked hotel"} isn't available for the new dates, so I've put back our matched hotel in ${x.b.city}.` }]);
            }
        });
        return () => ctrl?.abort();
    }, [hotelKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const price = useMemo(() => (inst && edit ? priceTrip(inst, edit) : null), [inst, edit]);
    const snapshot = useMemo(() => (edit && price ? tripSnapshot(edit, price, original) : null), [edit, price, original]);

    // Routy reads the trip; it steps aside while our own sheets are open
    useTripLeadSignals({
        trip: snapshot,
        agentStuck: misses >= 2,
        checkoutClosed,
        suppress: { tripChat: chatOpen, hotelPicker: !!picker, tripCheckout: checkout },
        dockOffset: isMobile ? 76 : 0,
    });

    // ── editing ───────────────────────────────────────────────────────────
    const commit = useCallback((ops) => {
        const cur = editRef.current;
        const res = applyOps(cur, ops, { origins: ORIGIN_CITIES, live: liveRef.current });
        if (res.changes.length) {
            setUndoStack((u) => [...u.slice(-19), cur]);
            setEdit(res.state);
            editRef.current = res.state;
        }
        return res;
    }, []);

    const undo = () => {
        setUndoStack((u) => {
            if (!u.length) return u;
            const prev = u[u.length - 1];
            setEdit(prev);
            editRef.current = prev;
            setChat((c) => [...c, { id: mid(), from: "agent", text: "Undone. Back to your previous version." }]);
            return u.slice(0, -1);
        });
    };

    const resetTrip = () => {
        if (!window.confirm("Reset to the original itinerary? Your changes will be lost.")) return;
        const fresh = { ...initialState(original), params: editRef.current.params, liveHotels: {} };
        setUndoStack((u) => [...u.slice(-19), editRef.current]);
        setEdit(fresh);
        editRef.current = fresh;
        setChat((c) => [...c, { id: mid(), from: "agent", text: "Reset to the original plan. Your dates and travellers are kept." }]);
    };

    const openHuman = (trigger = "tripChatHuman", intent = "trip_query") => {
        setChatOpen(false);
        setCheckout(false);
        requestBotOpen({ trigger, intent });
    };

    // preset = a menu option with its ops already decided, so nothing goes to the agent
    const sendToAgent = async (text, preset = null) => {
        const before = editRef.current;
        const beforePrice = price;
        setChat((c) => [...c, { id: mid(), from: "user", text }]);
        setAgentBusy(true);
        let res;
        if (preset) {
            res = { reply: preset.reply || "", ops: preset.ops || [], understood: true };
        } else {
            try {
                res = await runTripAgent({ message: text, state: before, history: chat, origins: ORIGIN_CITIES, live: liveRef.current });
            } catch {
                res = { reply: "Something went wrong on my side. Try again, or ask our team.", ops: [], understood: false };
            }
        }
        const applied = commit(res.ops);
        setAgentBusy(false);
        setAgentSugs(Array.isArray(res.suggestions) && res.suggestions.length ? res.suggestions.slice(0, 4) : null);

        // UI-only ops
        for (const u of applied.ui) {
            if (u.op === "open_hotel_picker") {
                const stay = stayBlocks(editRef.current.trip).find((x) => x.b.id === u.stay_id) || stayBlocks(editRef.current.trip)[0];
                if (stay) { setChatOpen(false); setPicker(stay.b.id); }
            }
            if (u.op === "handoff") openHuman("tripChatHuman", "trip_query");
        }

        const changed = applied.changes.length > 0;
        const helped = changed || applied.ui.length > 0;
        setMisses((m) => (helped ? 0 : m + 1));

        let priceNote = "";
        if (changed && inst && beforePrice) {
            if (structKey(applied.state.trip) === structKey(before.trip) && JSON.stringify(applied.state.params) === JSON.stringify(before.params)) {
                const p = priceTrip(inst, applied.state);
                const d = p.total - beforePrice.total;
                priceNote = `New total ${inr(p.total)}${d ? ` (${d > 0 ? "+" : "−"}${inr(Math.abs(d))})` : ""}.`;
            } else {
                priceNote = "Re-planning travel and stays for the new version.";
            }
        }
        const fallback = changed ? "Done." : applied.rejected.length ? "" : applied.ui.length ? "" : "I didn't catch that.";
        setChat((c) => [...c, {
            id: mid(), from: "agent",
            text: res.reply || fallback,
            changes: applied.changes, rejected: applied.rejected, priceNote,
        }]);
    };

    const suggestions = useMemo(() => {
        if (!edit) return [];
        if (agentSugs) return agentSugs;
        const t = edit.trip;
        const inTrip = new Set(allBlocks(t).map((x) => (x.b.name || "").toLowerCase()));
        const pool = (t.activity_pool || []).filter((p) => !inTrip.has(p.name.toLowerCase()));
        const anyPick = Object.keys(edit.hotelPicks).length > 0;
        const s = [
            anyPick ? "Show me other hotels" : "Cheaper hotels",
            t.days.length <= original.days.length ? "Add one more day" : "Remove the free day",
            pool[0] ? `Add ${pool[0].name}` : null,
            edit.params.pax === 2 ? "We are 4 people" : "Just the two of us",
            !anyPick ? "Upgrade the stay" : null,
        ];
        return s.filter(Boolean).slice(0, 4);
    }, [edit, original, agentSugs]);

    // "Remove the free day" isn't something the parser knows by name; map it to the right day
    const onSendSuggestion = (text) => {
        if (text === "Remove the free day") {
            const di = edit.trip.days.findIndex((d) => /^Free day/.test(d.title) && !d.blocks.some((b) => b.type !== "TIP"));
            return sendToAgent(di >= 0 ? `Remove day ${di + 1}` : "Remove one day");
        }
        return sendToAgent(text);
    };

    // Options-only chat: every choice maps straight to edit ops, built from the current version
    const chatOptions = useMemo(() => {
        if (!edit) return [];
        const t = edit.trip;
        const out = [];
        const stays = stayBlocks(t).filter((x) => x.b.lock !== "LOCKED");
        if (stays.length) {
            out.push({ label: "Cheaper hotels", ops: [{ op: "set_hotel_tier", tier: "budget" }] });
            out.push({ label: "Upgrade the stay", ops: [{ op: "set_hotel_tier", tier: "premium" }] });
            out.push({ label: "Pick a hotel", ops: [{ op: "open_hotel_picker", stay_id: stays[0].b.id }], reply: "Opening the hotel list." });
        }
        const freeDi = t.days.findIndex((d, i) => i > 0 && /^Free day/.test(d.title) && !d.blocks.some((b) => b.type !== "TIP"));
        if (freeDi > 0 && t.days.length > 2) out.push({ label: "Remove the free day", ops: [{ op: "remove_day", day: freeDi + 1 }] });
        else if (t.days.length < 10) out.push({ label: "Add one more day", ops: [{ op: "add_day" }] });
        const inTrip = new Set(allBlocks(t).map((x) => (x.b.name || "").toLowerCase()));
        const next = (t.activity_pool || []).find((p) => !inTrip.has(p.name.toLowerCase()));
        if (next) {
            const day = t.days.reduce((best, d0, i) => (i < t.days.length - 1 && d0.blocks.length < t.days[best].blocks.length ? i : best), 0) + 1;
            out.push({ label: `Add ${next.name}`, ops: [{ op: "add_activity", day, pool_id: next.id }] });
        }
        out.push(edit.params.routePref === "cheapest"
            ? { label: "Fastest way there", ops: [{ op: "set_params", route_pref: "fastest" }] }
            : { label: "Cheapest way there", ops: [{ op: "set_params", route_pref: "cheapest" }] });
        return out;
    }, [edit]);
    const pickOption = (o) => sendToAgent(o.label, o);

    if (!ENABLED || !original || !edit) return <Navigate to="/" replace />;

    const t = edit.trip;
    const day1 = new Date(edit.params.startDate + "T00:00:00");
    const pax = edit.params.pax;
    const stayFor = (id) => price?.stays.find((s) => s.id === id) || null;
    const pickerStay = picker ? stayFor(picker) : null;
    const pickerBlock = picker ? stayBlocks(t).find((x) => x.b.id === picker)?.b : null;
    const setParams = (p) => commit([{ op: "set_params", ...p }]);

    // Live hotels open the existing hotel booking page (same handoff as the trip planner)
    const bookStay = (s) => {
        if (!s) return;
        if (s.live) return handoffLiveHotel(navigate, s.live);
        const h = s.engine?.hotel;
        if (!h?.hotel_code) return;
        const { checkIn, checkOut } = stayDates(edit.params.startDate, s.dayIndex, s.nights);
        handoffLiveHotel(navigate, {
            hotel_code: h.hotel_code, name: h.hotel_name || s.name, rating: h.hotel_rating || h.rating,
            _raw: h, _room: h.room, check_in: checkIn, check_out: checkOut, no_of_rooms: s.rooms, image: (h.images || [])[0],
        });
    };
    const asideTop = Math.min(stickTop + barH + 8, viewH - asideH - 16);
    const edited = snapshot?.edits > 0 || t.days.length !== original.days.length;

    return (
        <div style={{ ...BASE.page, paddingTop: NAV_H, paddingBottom: isMobile ? 96 : 40 }}>
            <style>{BASE_CSS}</style>

            {/* Hero */}
            <section style={{
                ...BASE.hero,
                backgroundImage: t.cover
                    ? `linear-gradient(180deg, rgba(15,23,42,.15) 0%, rgba(15,23,42,.85) 100%), url(${t.cover})`
                    : `linear-gradient(180deg, rgba(15,23,42,0) 30%, rgba(15,23,42,.55) 100%), ${t.cover_gradient}`,
            }}>
                <div style={BASE.heroInner}>
                    <Link to="/trips" style={S.allTrips}>← All trips</Link>
                    <div style={BASE.creatorChip}>
                        <span style={{ ...BASE.miniAvatar, width: 24 }}><Sparkles size={12} fill="#fff" color="#fff" /></span>
                        Curated by {t.curator} · editable
                    </div>
                    <h1 style={{ color: "#fff", fontSize: "clamp(26px, 4.5vw, 38px)", margin: "10px 0 6px", lineHeight: 1.1 }}>{t.title}</h1>
                    <div style={{ color: "rgba(255,255,255,.88)", fontSize: 15 }}>
                        {t.days.length} days · {t.subtitle.split("·").slice(1).join("·").trim() || t.subtitle}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
                        {t.vibe.map((v) => <span key={v} style={BASE.heroTag}>{v}</span>)}
                        {t.best_months && <span style={BASE.heroTag}>Best {t.best_months}</span>}
                    </div>
                </div>
            </section>

            {/* Controls: solid background once stuck under the Navbar */}
            <StickyControls top={stickTop} onHeight={setBarH} compact={isMobile}>
                {isMobile ? (
                    // Phone: one tight card, three fields in a row, optimise + undo below, status on one scrolling line
                    <div style={S.mCard}>
                        <div style={S.mRow}>
                            <label style={{ ...S.mField, flex: "1.15 1 0" }}>
                                <span style={S.mLabel}>From</span>
                                <select value={origin.name} onChange={(e) => setParams({ origin: e.target.value })} style={S.mInput}>
                                    {ORIGIN_CITIES.map((c) => <option key={c.name}>{c.name}</option>)}
                                </select>
                            </label>
                            <label style={{ ...S.mField, flex: "1.25 1 0" }}>
                                <span style={S.mLabel}>Day 1</span>
                                <input type="date" value={edit.params.startDate} min={toISO(addDays(new Date(), 1))}
                                    onClick={openDatePicker}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openDatePicker(e); }}
                                    onChange={(e) => e.target.value && setParams({ start_date: e.target.value })}
                                    style={{ ...S.mInput, cursor: "pointer" }} aria-label={`Day 1 in ${t.entry.city}`} />
                            </label>
                            <div style={{ ...S.mField, flex: "0 0 auto" }}>
                                <span style={S.mLabel}>People</span>
                                <div style={S.mPax}>
                                    <button style={S.mStep} onClick={() => pax > 1 && setParams({ pax: pax - 1 })} aria-label="Fewer travellers">−</button>
                                    <b style={{ minWidth: 14, textAlign: "center", fontSize: 14 }}>{pax}</b>
                                    <button style={S.mStep} onClick={() => pax < t.max_group && setParams({ pax: pax + 1 })} aria-label="More travellers">+</button>
                                </div>
                            </div>
                        </div>
                        <div style={{ ...S.mRow, alignItems: "center", marginTop: 6 }}>
                            <div style={{ display: "flex", gap: 4 }} role="group" aria-label="Optimise for">
                                {[["best", "Best"], ["cheapest", "Cheapest"], ["fastest", "Fastest"]].map(([k, l]) => (
                                    <button key={k} onClick={() => setParams({ route_pref: k })} style={{ ...S.mChip, ...(edit.params.routePref === k ? S.mChipOn : null) }}>{l}</button>
                                ))}
                            </div>
                            <span style={{ flex: 1 }} />
                            {undoStack.length > 0 && <button onClick={undo} style={S.mIconBtn} aria-label="Undo"><Undo2 size={14} /></button>}
                            {edited && <button onClick={resetTrip} style={S.mIconBtn} aria-label="Reset trip"><RotateCcw size={14} /></button>}
                        </div>
                        <div style={S.mStatus} className="ct-noscrollbar">
                            {loading
                                ? <SearchSteps compact steps={steps} origin={origin.name} trip={t} liveStatus={liveStatus} live={live} fallback={progress} />
                                : inst && <span style={{ whiteSpace: "nowrap" }}>
                                    <b style={{ color: GREEN }}>✓</b> From <b>{origin.name}</b>
                                    {price?.anyEstimated ? <span style={{ color: AMBER }}> · some prices estimated</span> : <span style={{ color: GREEN }}> · live prices</span>}
                                </span>}
                            {edited && <span style={{ ...S.editedPill, flexShrink: 0 }}><Pencil size={10} /> Your version</span>}
                        </div>
                    </div>
                ) : (<>
                <div style={BASE.controls}>
                    <Field label="Travelling from">
                        <select value={origin.name} onChange={(e) => setParams({ origin: e.target.value })} style={BASE.input}>
                            {ORIGIN_CITIES.map((c) => <option key={c.name}>{c.name}</option>)}
                        </select>
                    </Field>
                    <Field label={`Day 1 in ${t.entry.city}`}>
                        <input type="date" value={edit.params.startDate} min={toISO(addDays(new Date(), 1))}
                            onClick={openDatePicker}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openDatePicker(e); }}
                            onChange={(e) => e.target.value && setParams({ start_date: e.target.value })} style={{ ...BASE.input, cursor: "pointer" }} />
                    </Field>
                    <Field label="Travellers">
                        <div style={{ display: "flex", alignItems: "center", gap: 10, height: 40 }}>
                            <button style={BASE.step} onClick={() => pax > 1 && setParams({ pax: pax - 1 })} aria-label="Fewer travellers">−</button>
                            <b style={{ minWidth: 18, textAlign: "center" }}>{pax}</b>
                            <button style={BASE.step} onClick={() => pax < t.max_group && setParams({ pax: pax + 1 })} aria-label="More travellers">+</button>
                        </div>
                    </Field>
                    <Field label="Optimise for">
                        <div style={{ display: "flex", gap: 4, height: 40, alignItems: "center" }}>
                            {[["best", "Best"], ["cheapest", "Cheapest"], ["fastest", "Fastest"]].map(([k, l]) => (
                                <button key={k} onClick={() => setParams({ route_pref: k })} style={{ ...BASE.chip, ...(edit.params.routePref === k ? BASE.chipOn : null) }}>{l}</button>
                            ))}
                        </div>
                    </Field>
                </div>
                <div style={{ ...BASE.status, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    {loading
                        ? <SearchSteps steps={steps} origin={origin.name} trip={t} liveStatus={liveStatus} live={live} fallback={progress} />
                        : inst && <span>
                            <b style={{ color: GREEN }}>✓</b> Planned from <b>{origin.name}</b>
                            {price?.anyEstimated ? <span style={{ color: AMBER }}> · some prices estimated</span> : <span style={{ color: GREEN }}> · live prices</span>}
                        </span>}
                    {edited && <span style={S.editedPill}><Pencil size={11} /> Your version</span>}
                    <span style={{ flex: 1 }} />
                    {undoStack.length > 0 && <button onClick={undo} style={S.linkBtn}><Undo2 size={13} /> Undo</button>}
                    {edited && <button onClick={resetTrip} style={S.linkBtn}><RotateCcw size={13} /> Reset</button>}
                </div>
                </>)}
            </StickyControls>

            <main style={{ ...BASE.main, gridTemplateColumns: "minmax(0, 1fr) 360px" }} className="ct-main">
                {/* Timeline */}
                <div style={{ minWidth: 0, opacity: loading ? 0.6 : 1, transition: "opacity .2s" }}>
                    {inst?.warnings?.map((w, i) => (
                        <div key={i} style={{ ...BASE.warn, ...(w.kind === "season" ? BASE.warnSeason : null) }}>
                            {w.kind === "season" ? "⚠️ " : "🗓 "}{w.text}
                        </div>
                    ))}

                    <Section title="Getting there" sub={`${origin.name} → ${t.entry.name}`} badge="Live for you">
                        {inst && <EdgeLeg edge={inst.outbound} options={inst.outOptions} onPick={setOutIdx} from={origin} to={{ name: t.entry.city, lat: t.entry.lat, lng: t.entry.lng }} cityA={origin.name} cityB={t.entry.city} pax={pax} startBy={t.entry.start_by} />}
                        {inst && <LegBook edge={inst.outbound} pax={pax} navigate={navigate} from={origin} to={{ name: t.entry.city, lat: t.entry.lat, lng: t.entry.lng }} date={toISO(addDays(day1, -1))} />}
                        {inst?.outbound?.needsArrivalNight && (
                            <div style={BASE.subNote}>🏨 Extra night in {t.entry.city} before Day 1 (estimated, picked at checkout)</div>
                        )}
                    </Section>

                    {t.days.map((d, di) => {
                        const inDay = new Set(d.blocks.map((b) => (b.name || "").toLowerCase()));
                        const inTrip = new Set(allBlocks(t).map((x) => (x.b.name || "").toLowerCase()));
                        const pool = (t.activity_pool || []).filter((p) => !inTrip.has(p.name.toLowerCase()) && !inDay.has(p.name.toLowerCase()));
                        return (
                            <Section key={`${d.day}-${d.title}`} title={`Day ${d.day} · ${d.title}`} sub={fmtDay(addDays(day1, di))}>
                                {d.blocks.length === 0 && <div style={BASE.subNote}>Free day. Add something below or keep it slow.</div>}
                                {d.blocks.map((b) => (
                                    <Block key={b.id} b={b} pax={pax} stay={b.type === "STAY" ? stayFor(b.id) : null}
                                        onRemove={() => commit([{ op: "remove_block", block_id: b.id }])}
                                        onChangeHotel={() => setPicker(b.id)}
                                        onBookHotel={() => bookStay(stayFor(b.id))} />
                                ))}
                                {addingDay === di ? (
                                    <div style={S.poolBox}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                            <b style={{ fontSize: 13 }}>Add to Day {d.day}</b>
                                            <button onClick={() => setAddingDay(null)} style={S.xBtn} aria-label="Close"><X size={14} /></button>
                                        </div>
                                        {pool.length === 0 && <div style={{ fontSize: 13, color: MUTED }}>Everything on our list is already in. Ask the AI for something specific.</div>}
                                        {pool.map((p) => (
                                            <button key={p.id} style={S.poolRow} onClick={() => { commit([{ op: "add_activity", day: d.day, pool_id: p.id }]); setAddingDay(null); }}>
                                                <span style={{ fontSize: 16 }}>{BLOCK_ICON[p.type] || "✨"}</span>
                                                <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                                                    <span style={{ display: "block", fontWeight: 600, fontSize: 13, color: INK }}>{p.name}</span>
                                                    <span style={{ display: "block", fontSize: 12, color: MUTED }}>{[p.time, p.note].filter(Boolean).join(" · ")}</span>
                                                </span>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: p.approx_pp ? AMBER : GREEN, whiteSpace: "nowrap", textAlign: "right" }}>{p.approx_pp ? <>Pay at venue<br /><span style={{ fontWeight: 500, color: MUTED }}>about {inr(p.approx_pp)} pp</span></> : "Free entry"}</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <button style={S.addBtn} onClick={() => setAddingDay(di)}><Plus size={14} /> Add activity</button>
                                )}
                            </Section>
                        );
                    })}

                    <Section title="Getting back" sub={`${t.exit.name} → ${origin.name} · ${fmtDay(addDays(day1, t.exit.day_offset))}`} badge="Live for you">
                        {inst && <EdgeLeg edge={inst.inbound} options={inst.retOptions} onPick={setRetIdx} from={{ name: t.exit.city, lat: t.exit.lat, lng: t.exit.lng }} to={origin} cityA={t.exit.city} cityB={origin.name} pax={pax} />}
                        {inst && <LegBook edge={inst.inbound} pax={pax} navigate={navigate} from={{ name: t.exit.city, lat: t.exit.lat, lng: t.exit.lng }} to={origin} date={toISO(addDays(day1, t.exit.day_offset))} />}
                    </Section>

                    <div style={BASE.legend}>
                        <span><b>Curator pick</b> stays in the plan, can move days</span>
                        <span><b>Preferred</b> booked if available, else same tier nearby</span>
                        <span><b>Flexible</b> yours to change or drop</span>
                    </div>
                </div>

                {/* Price + AI chat */}
                {/* Sits below the sticky controls. The chat keeps its full size; if the screen is short, the column scrolls instead of shrinking the chat. */}
                <aside ref={asideRef} style={{ ...S.aside, top: asideTop, height: "auto", minHeight: 0 }} className="cp-aside">
                    <div style={{ ...BASE.priceCard, flexShrink: 0 }}>
                        <div style={{ fontSize: 12, color: MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Your trip from {origin.name}</div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                            <div style={{ fontSize: 32, fontWeight: 900, color: INK }}>{price ? inr(price.perPerson) : "…"}</div>
                            <div style={{ color: MUTED, fontSize: 13 }}>per person</div>
                        </div>
                        <div style={{ color: MUTED, fontSize: 13 }}>{price ? `${inr(price.total)} total · ${pax} traveller${pax > 1 ? "s" : ""} · ${price.rooms} room${price.rooms > 1 ? "s" : ""}` : "Calculating"}</div>
                        {price && (
                            <details style={{ marginTop: 10 }}>
                                <summary style={{ fontSize: 12, fontWeight: 700, color: ORANGE, cursor: "pointer" }}>Price breakdown</summary>
                                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                                    {price.lines.map((l) => (
                                        <div key={l.k} style={BASE.line}>
                                            <span style={BASE.lineLabel}>{l.label}{l.estimated && <span style={BASE.est}>est.</span>}</span>
                                            <span>{l.amount < 0 ? "−" : ""}{inr(Math.abs(l.amount))}</span>
                                        </div>
                                    ))}
                                    <div style={BASE.line}><span style={BASE.lineLabel}>Itinerary, support and fees</span><span>{inr(price.fees)}</span></div>
                                </div>
                            </details>
                        )}
                        {price?.payAtVenue?.length > 0 && (
                            <div style={{ fontSize: 11, color: AMBER, marginTop: 8 }}>Activities aren't included. You pay for them at the venue.</div>
                        )}
                        <button disabled={!price || loading} onClick={() => setCheckout(true)} style={{ ...BASE.bookBtn, marginTop: 12, opacity: !price || loading ? 0.5 : 1 }}>
                            Book this trip →
                        </button>
                        <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginTop: 8 }}>One-tap booking for the whole trip · launching soon</div>
                    </div>
                    {!isMobile && (
                        <div style={{ height: 540, flexShrink: 0, display: "flex" }}>
                            <div style={{ flex: 1, display: "flex" }}>
                                <TripAgentChat
                                    messages={chat} busy={agentBusy} suggestions={suggestions} options={CHAT_FREEFORM ? null : chatOptions} onPick={pickOption} canUndo={undoStack.length > 0}
                                    onSend={onSendSuggestion} onUndo={undo} onHuman={() => openHuman()}
                                    fill
                                />
                            </div>
                        </div>
                    )}
                </aside>
            </main>

            {/* Mobile sticky bar */}
            {isMobile && (
                <div style={{ ...BASE.mobileBar, display: "flex", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 900, fontSize: 19 }}>{price ? inr(price.perPerson) : "…"}</div>
                        <div style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>per person · {t.days.length} days</div>
                    </div>
                    <span style={{ flex: 1 }} />
                    <button onClick={() => setChatOpen(true)} style={S.aiBtn}><Sparkles size={15} fill={ORANGE} /> Edit</button>
                    <button disabled={!price || loading} onClick={() => setCheckout(true)} style={{ ...BASE.bookBtn, width: "auto", marginTop: 0, padding: "12px 18px", opacity: !price || loading ? 0.5 : 1 }}>Book →</button>
                </div>
            )}

            {isMobile && chatOpen && (
                <TripAgentChat
                    variant="sheet" messages={chat} busy={agentBusy} suggestions={suggestions} options={CHAT_FREEFORM ? null : chatOptions} onPick={pickOption} canUndo={undoStack.length > 0}
                    onSend={onSendSuggestion} onUndo={undo} onHuman={() => openHuman()} onClose={() => setChatOpen(false)}
                />
            )}

            <HotelPickerSheet
                open={!!pickerStay}
                stay={pickerStay}
                options={pickerBlock ? hotelOptionsFor(t, pickerBlock.city) : []}
                liveOptions={picker ? live[picker] || [] : []}
                liveLoading={picker ? liveStatus[picker] === "loading" : false}
                curatorTier={pickerBlock?.tier}
                onClose={() => setPicker(null)}
                onPick={(code) => {
                    const res = commit([{ op: "swap_hotel", stay_id: picker, hotel_code: code }]);
                    if (res.rejected.length) window.alert(res.rejected[0]);
                    setPicker(null);
                }}
            />

            {checkout && (
                <EarlyAccessSheet
                    dest={t.title.split(":")[0]}
                    trip={snapshot}
                    onDone={() => setEarlySignedUp(true)}
                    onClose={() => { setCheckout(false); if (!earlySignedUp) setCheckoutClosed(true); }}
                    onTalkToUs={() => openHuman("tripReserve", "trip_booking")}
                />
            )}
        </div>
    );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

// Native date inputs only open from the calendar icon in Chrome; open the picker on any click in the field.
// The Field label wraps the input, so clicking the label text works too.
const openDatePicker = (e) => { try { e.currentTarget.showPicker?.(); } catch { /* not allowed here: field still focuses */ } };

// Three live status chips while the trip is being planned: getting there, getting back, stays
// Book buttons for the selected getting-there / getting-back option, one per segment, always shown.
//  - bus with result_idx / trace_id  → /book-bus in a new tab (same as search results)
//  - segment with booking_link       → that link
//  - anything else (flights without a link, trains, estimated routes) → the route search page for
//    that exact segment and date in a new tab, where the normal Book buttons work
// Changes as soon as the traveller picks another option above.
const segSearchUrl = (seg, { pax, fallbackFrom, fallbackTo, date }) => {
    const m = segMode(seg);
    const type = m === "flight" ? "airport" : m === "train" ? "station" : "";
    const d = (seg.origin_dep_dt || "").split("T")[0] || date;
    const q = new URLSearchParams({
        from: seg.origin_name || fallbackFrom?.name || "",
        to: seg.dest_name || fallbackTo?.name || "",
        date: d,
        adults: String(pax), children: "0", infants: "0",
        modes: m === "flight" ? "Flight" : m === "train" ? "Train" : "Bus",
        seatClasses: m === "train" ? "3A" : "Economy",
        nonstop: "1",
    });
    if (seg.origin_code) { q.set("scode", seg.origin_code); if (type) q.set("stype", type); }
    else if (fallbackFrom?.lat) { q.set("slat", fallbackFrom.lat); q.set("slng", fallbackFrom.lng); }
    if (seg.dest_code) { q.set("dcode", seg.dest_code); if (type) q.set("dtype", type); }
    else if (fallbackTo?.lat) { q.set("dlat", fallbackTo.lat); q.set("dlng", fallbackTo.lng); }
    return `/search-form?${q.toString()}`;
};

const MODE_WORD = { flight: "flight", train: "train", bus: "bus", cab: "cab", shared_cab: "cab" };

// Consecutive flight segments with the same booking link (or none) are one ticket, e.g. a
// one-stop flight (route type 2): one row, one Book button, searched with stops allowed.
const groupSegments = (segs) => {
    const groups = [];
    for (const seg of segs) {
        const m = segMode(seg);
        const prev = groups[groups.length - 1];
        if (prev && m === "flight" && prev.m === "flight" && (prev.segs[0].booking_link || "") === (seg.booking_link || "")) {
            prev.segs.push(seg);
        } else {
            groups.push({ m, segs: [seg] });
        }
    }
    return groups;
};

function LegBook({ edge, pax, navigate, from, to, date }) {
    if (!edge || edge.skipped || !edge.route) return null;
    const segs = (edge.route.nodes || []).map((n) => n.segment || {});
    const estimated = !!edge.route.estimated;
    const items = groupSegments(segs).map(({ m, segs: g }) => {
        const first = g[0], last = g[g.length - 1];
        const word = MODE_WORD[m] || "ticket";
        const name = g.length > 1
            ? `${first.carrier || "Flight"} ${g.map((x) => x.flight_num).filter(Boolean).join(" + ")}`.trim()
            : segName(first);
        const via = g.length > 1 ? g.slice(0, -1).map((x) => x.dest_name).filter(Boolean).join(", ") : "";
        // One search covering the whole ticket: first origin to last destination, stops allowed if it has any
        const whole = { ...first, dest_name: last.dest_name, dest_code: last.dest_code };
        const search = () => {
            let url = segSearchUrl(whole, { pax, fallbackFrom: from, fallbackTo: to, date });
            if (g.length > 1) url = url.replace("nonstop=1", "nonstop=0");
            window.open(url, "_blank", "noopener");
        };
        const base = { m, name, via, dep: first.origin_dep_dt };
        if (!estimated && m === "bus" && (first.result_idx || first.trace_id)) {
            return {
                ...base, label: "Book bus", primary: true,
                go: () => openInNewTab(navigate, "/book-bus", {
                    state: {
                        busInfo: { segment: { ...first, boarding_points: first.boarding_points || [], dropping_points: first.dropping_points || [], bus_test: first.bus_test ?? false } },
                        searchParams: { from: first.origin_name, to: first.dest_name, date: first.origin_dep_dt?.split("T")[0], adults: pax, children: 0 },
                    },
                }),
            };
        }
        if (!estimated && first.booking_link) {
            return { ...base, label: `Book ${word}`, primary: true, go: () => window.open(first.booking_link, "_blank", "noopener") };
        }
        // No direct booking data on this segment: book it from the live search for the same leg
        return { ...base, label: estimated ? `Find ${word === "bus" ? "buses" : `${word}s`}` : `Book ${word}`, primary: !estimated, go: search };
    });
    return (
        <div style={S.legBook}>
            {estimated && <div style={{ fontSize: 12, color: AMBER }}>Live fares weren't available for this leg. Search it to book.</div>}
            {items.map((it, i) => (
                <div key={i} style={S.legBookRow}>
                    <span style={{ fontSize: 15 }} aria-hidden="true">{MODE_ICON[it.m]}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {it.name}
                        {it.dep && <span style={{ fontWeight: 500, color: MUTED }}> · {fmtTime(it.dep)}</span>}
                        {it.via && <span style={{ fontWeight: 500, color: MUTED }}> · 1 stop, {it.via}</span>}
                    </span>
                    <button onClick={it.go} style={it.primary ? S.legBookBtn : S.legFindBtn}>{it.label}</button>
                </div>
            ))}
        </div>
    );
}

function SearchSteps({ steps, origin, trip, liveStatus, live, fallback, compact = false }) {
    if (compact) return <CompactSteps steps={steps} liveStatus={liveStatus} live={live} fallback={fallback} />;
    const stayCities = [...new Set(stayBlocks(trip).map((x) => x.b.city))].join(" and ");
    const liveDone = Object.values(liveStatus).length > 0 && Object.values(liveStatus).every((v) => v === "done");
    const liveCount = Object.values(live).reduce((n, l) => n + (l?.length || 0), 0);
    const edge = (st, searching, found, estimated) => {
        if (!st || st.state === "start") return { busy: true, text: searching };
        if (st.state === "skipped") return { text: "No travel needed" };
        if (st.source === "estimate" || !st.count) return { warn: true, text: estimated };
        return { text: found(st.count) };
    };
    const opt = (n) => `${n} option${n === 1 ? "" : "s"}`;
    const items = [
        { k: "out", icon: "🧭", ...edge(steps.out, `Searching routes from ${origin}`, (n) => `${opt(n)} to ${trip.entry.city}`, "Route there estimated") },
        { k: "back", icon: "↩️", ...edge(steps.back, `Searching routes back to ${origin}`, (n) => `${opt(n)} back to ${origin}`, "Route back estimated") },
        (() => {
            const st = steps.stays;
            if (!st || st.state === "start" || !liveDone) return { k: "stays", icon: "🏨", busy: true, text: `Checking stays in ${stayCities}` };
            if (liveCount) return { k: "stays", icon: "🏨", text: `${liveCount} hotels available now` };
            return { k: "stays", icon: "🏨", warn: st.source === "estimate", text: st.source === "estimate" ? "Stays estimated" : "Stays ready" };
        })(),
    ];
    if (!Object.keys(steps).length && fallback) {
        // Engine hasn't reported steps yet (debounce window)
        return <span className="ct-pulse">● {fallback}…</span>;
    }
    return (
        <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }} aria-live="polite">
            {items.map((it) => (
                <span key={it.k} style={{ ...S.stepChip, ...(it.busy ? S.stepBusy : it.warn ? S.stepWarn : S.stepDone) }}>
                    <span aria-hidden="true">{it.busy ? <span className="cp-dot" /> : it.warn ? "•" : "✓"}</span>
                    <span aria-hidden="true">{it.icon}</span> {it.text}{it.busy ? "…" : ""}
                </span>
            ))}
        </span>
    );
}

// Phone version: same three steps, short words, one line
function CompactSteps({ steps, liveStatus, live, fallback }) {
    if (!Object.keys(steps).length && fallback) return <span className="ct-pulse" style={{ whiteSpace: "nowrap" }}>● Searching routes and stays…</span>;
    const liveDone = Object.values(liveStatus).length > 0 && Object.values(liveStatus).every((v) => v === "done");
    const liveCount = Object.values(live).reduce((n, l) => n + (l?.length || 0), 0);
    const edge = (st, label) => {
        if (!st || st.state === "start") return { busy: true, text: label };
        if (st.state === "skipped") return { text: `${label}: none` };
        if (st.source === "estimate" || !st.count) return { warn: true, text: `${label} est.` };
        return { text: `${label} ${st.count}` };
    };
    const st = steps.stays;
    const items = [
        { k: "out", ...edge(steps.out, "Route there") },
        { k: "back", ...edge(steps.back, "Route back") },
        (!st || st.state === "start" || !liveDone)
            ? { k: "stays", busy: true, text: "Stays" }
            : liveCount ? { k: "stays", text: `${liveCount} hotels` } : { k: "stays", warn: st.source === "estimate", text: st.source === "estimate" ? "Stays est." : "Stays" },
    ];
    return (
        <span style={{ display: "inline-flex", gap: 4, flexShrink: 0 }} aria-live="polite">
            {items.map((it) => (
                <span key={it.k} style={{ ...S.stepChip, ...S.mStepChip, ...(it.busy ? S.stepBusy : it.warn ? S.stepWarn : S.stepDone) }}>
                    <span aria-hidden="true">{it.busy ? <span className="cp-dot" /> : it.warn ? "•" : "✓"}</span>{it.text}{it.busy ? "…" : ""}
                </span>
            ))}
        </span>
    );
}

const Lock = ({ l }) => (l ? <span style={{ ...BASE.lock, ...(l === "LOCKED" ? BASE.lockLocked : l === "PREFERRED" ? BASE.lockPref : BASE.lockFlex) }}>{LOCK_LABEL[l]}</span> : null);

function Block({ b, pax, stay, onRemove, onChangeHotel, onBookHotel }) {
    if (b.type === "TIP") return <div style={BASE.tip}>💡 {b.text}</div>;
    if (b.type === "MOVE") {
        return (
            <div style={BASE.row}>
                <span style={BASE.rowIcon}>{MODE_ICON[b.mode] || "🧭"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={BASE.rowTitle}>{b.from} → {b.to}<Lock l={b.lock} /></div>
                    <div style={BASE.rowSub}>{b.label} · dep {b.depart} · {b.duration}</div>
                </div>
                <span style={BASE.rowPrice}>{inr(b.price_pp * pax)}</span>
            </div>
        );
    }
    if (b.type === "STAY") {
        return (
            <div style={{ ...BASE.row, background: "#FFFBF5", borderColor: "#FDE7D3", flexWrap: "wrap" }}>
                <span style={BASE.rowIcon}>🏨</span>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <div style={BASE.rowTitle}>{stay ? stay.name : (b.preferred || `Stay in ${b.city}`)}<Lock l={b.lock} /></div>
                    <div style={BASE.rowSub}>
                        {b.nights} night{b.nights > 1 ? "s" : ""} in {b.city}
                        {stay && ` · ${stay.rooms} room${stay.rooms > 1 ? "s" : ""}`}
                        {(stay?.live || (!stay?.picked && stay?.engine?.hotel?.hotel_code)) && <span style={{ color: GREEN }}> · live rate, book instantly</span>}
                        {stay?.picked && !stay.live && <span style={{ color: "#0369A1" }}> · {TIER_LABEL[stay.picked.tier]} pick, confirmed by our team</span>}
                        {!stay?.picked && stay?.engine?.hotel?.matched === "preferred" && <span style={{ color: GREEN }}> · curator's hotel available</span>}
                        {!stay?.picked && stay?.engine?.hotel?.matched === "substitute" && <span style={{ color: AMBER }}> · {b.preferred} unavailable, same tier nearby</span>}
                        {!stay?.picked && stay?.engine?.estimated && <span style={{ color: AMBER }}> · estimated</span>}
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
                    <span style={BASE.rowPrice}>{stay ? inr(stay.total) : "…"}</span>
                    {b.lock !== "LOCKED" && <button onClick={onChangeHotel} style={S.changeHotel}>Change</button>}
                    {stay?.liveBookable && onBookHotel && <button onClick={onBookHotel} style={S.bookHotel}>Book</button>}
                </div>
            </div>
        );
    }
    return (
        <div style={{ ...BASE.row, ...(b.added ? { borderColor: "#BBF7D0", background: "#F7FEF9" } : null) }}>
            <span style={BASE.rowIcon}>{BLOCK_ICON[b.type]}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={BASE.rowTitle}>{b.name}<Lock l={b.lock} />{b.added && <span style={{ ...BASE.lock, background: "#DCFCE7", color: GREEN }}>Added by you</span>}</div>
                {(b.time || b.note) && <div style={BASE.rowSub}>{b.time}{b.time && b.note ? " · " : ""}{b.note}</div>}
                {b.type === "EXPERIENCE" && b.approx_pp > 0 && (
                    <div style={{ ...BASE.rowSub, color: AMBER }}>Not booked by RouteChef. Pay at the venue, about {inr(b.approx_pp)} per person.</div>
                )}
            </div>
            {b.type === "EXPERIENCE" && <span style={S.venueTag} title="RouteChef doesn't book or charge for activities">{b.approx_pp > 0 ? "Pay at venue" : "Free entry"}</span>}
            {b.lock !== "LOCKED" && (
                <button onClick={onRemove} style={S.xBtn} aria-label={`Remove ${b.name}`} title="Remove"><X size={14} /></button>
            )}
        </div>
    );
}

/* ── styles ─────────────────────────────────────────────────────────────── */
const S = {
    stepChip: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 99, border: "1px solid", whiteSpace: "nowrap" },
    stepBusy: { color: ORANGE, background: "#FFF4EE", borderColor: "#FFD6C2" },
    stepDone: { color: GREEN, background: "#ECFDF5", borderColor: "#BBF7D0" },
    stepWarn: { color: AMBER, background: "#FFFBEB", borderColor: "#FDE68A" },
    // phone controls
    mCard: { background: "#fff", borderRadius: 14, border: "1px solid #EEE", boxShadow: "0 10px 26px -16px rgba(15,23,42,.4)", padding: "8px 10px" },
    mRow: { display: "flex", gap: 8, minWidth: 0 },
    mField: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    mLabel: { fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 },
    mInput: { height: 34, border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "0 6px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit", background: "#fff", color: "#0F172A", width: "100%", minWidth: 0, boxSizing: "border-box" },
    mPax: { display: "flex", alignItems: "center", gap: 6, height: 34 },
    mStep: { width: 28, height: 28, borderRadius: "50%", border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: 0 },
    mChip: { border: "1.5px solid #E2E8F0", background: "#fff", borderRadius: 99, padding: "4px 9px", fontSize: 11.5, fontWeight: 600, color: MUTED, cursor: "pointer", fontFamily: "inherit" },
    mChipOn: { border: `1.5px solid ${ORANGE}`, color: ORANGE, background: "#FFF4EE" },
    mIconBtn: { width: 30, height: 28, border: "none", background: "transparent", color: MUTED, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
    mStatus: { display: "flex", alignItems: "center", gap: 6, marginTop: 6, paddingTop: 6, borderTop: "1px solid #F1F5F9", fontSize: 12, color: MUTED, overflowX: "auto", whiteSpace: "nowrap", WebkitOverflowScrolling: "touch" },
    mStepChip: { fontSize: 11, padding: "2px 8px", gap: 4 },
    legBook: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 },
    legBookRow: { display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #F1F5F9", borderRadius: 10, padding: "8px 10px" },
    legFindBtn: { border: `1.5px solid #FFD6C2`, background: "#fff", color: ORANGE, fontWeight: 700, fontSize: 12.5, padding: "7px 13px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", minHeight: 34 },
    legBookBtn: { border: "none", background: ORANGE, color: "#fff", fontWeight: 700, fontSize: 12.5, padding: "8px 14px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", minHeight: 34 },
    venueTag: { fontSize: 10.5, fontWeight: 700, color: AMBER, background: "#FEF3C7", padding: "3px 8px", borderRadius: 99, whiteSpace: "nowrap", flexShrink: 0 },
    notIncluded: { marginTop: 10, border: "1px dashed #FCD34D", background: "#FFFBEB", borderRadius: 10, padding: "10px 12px" },
    allTrips: { display: "inline-block", color: "rgba(255,255,255,.9)", textDecoration: "none", fontSize: 13, fontWeight: 700, marginBottom: 10 },
    bookHotel: { border: "none", background: ORANGE, color: "#fff", fontWeight: 700, fontSize: 12, padding: "7px 12px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", minHeight: 32 },
    aside: { position: "sticky", top: NAV_H + 12, height: `calc(100vh - ${NAV_H + 110}px)`, minHeight: 560, display: "flex", flexDirection: "column", gap: 12 },
    editedPill: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: "#6B3FA0", background: "#F3E8FF", padding: "3px 8px", borderRadius: 99 },
    linkBtn: { display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: MUTED, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: "4px 2px" },
    xBtn: { width: 30, height: 30, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: MUTED, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
    addBtn: { alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, border: "1.5px dashed #FFD6C2", background: "#fff", color: "#C2410C", fontWeight: 700, fontSize: 12, padding: "8px 12px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", minHeight: 36 },
    poolBox: { border: "1.5px solid #FFD6C2", background: "#FFFAF7", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
    poolRow: { display: "flex", alignItems: "center", gap: 10, width: "100%", border: "1px solid #F1F5F9", background: "#fff", borderRadius: 10, padding: "9px 10px", cursor: "pointer", fontFamily: "inherit" },
    changeHotel: { border: "1.5px solid #FFD6C2", background: "#fff", color: ORANGE, fontWeight: 700, fontSize: 12, padding: "6px 12px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", minHeight: 32 },
    aiBtn: { display: "inline-flex", alignItems: "center", gap: 6, border: `1.5px solid ${ORANGE}`, background: "#FFF4EE", color: ORANGE, fontWeight: 800, fontSize: 14, padding: "11px 14px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit" },
};

const BASE_CSS = `
@keyframes ctPulse{0%,100%{opacity:1}50%{opacity:.4}}
.ct-pulse{animation:ctPulse 1.1s ease-in-out infinite;color:${ORANGE};font-weight:600}
.cp-dot{display:inline-block;width:7px;height:7px;border-radius:50%;background:${ORANGE};animation:ctPulse 1s ease-in-out infinite}
@media (prefers-reduced-motion: reduce){ .cp-dot,.ct-pulse{animation:none} }
.ct-noscrollbar{scrollbar-width:none}.ct-noscrollbar::-webkit-scrollbar{display:none}
@media (max-width: 860px){
  .ct-main{grid-template-columns:minmax(0,1fr) !important}
  .cp-aside{position:static !important;height:auto !important;min-height:0 !important}
}
`;
